import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../../Axios";
import { FaEdit, FaTrash } from "react-icons/fa";

const ManageMouList = () => {
  const [mous, setMous] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMou, setSelectedMou] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    fetchMous();
  }, []);

  const fetchMous = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${baseURL}/api/v1/mouList/all`);
      setMous(response.data.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred while fetching Mous."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Mou?")) {
      setLoading(true);
      try {
        await api.delete(`${baseURL}/api/v1/mouList/delete/${id}`);
        toast.success("Mou deleted successfully!");
        fetchMous(); // Refresh the list after deletion
      } catch (error) {
        toast.error(
          error.response?.data?.message || "An error occurred while deleting Mou."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdate = (mou) => {
    setSelectedMou(mou);
    setShowModal(true);
  };

  const handleModalSubmit = async () => {
    if (selectedMou) {
      setLoading(true);
      try {
        await api.put(`${baseURL}/api/v1/mouList/update/${selectedMou._id}`, {
          listHead: selectedMou.listHead,
          link: selectedMou.link,
        });
        toast.success("Mou updated successfully!");
        fetchMous(); // Refresh the list after update
        setShowModal(false);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "An error occurred while updating Mou."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const navigateToAddMou = () => {
    navigate("/admin/addmou");
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      <ToastContainer position="top-center" autoClose={3000} />

      <div className="bg-[#00BFA5] text-black text-center font-bold py-4 px-6 max-w-4xl mx-auto text-xl sm:text-2xl md:text-3xl rounded-lg shadow-lg">
        Manage Mou List
      </div>

      <div className="flex justify-start mb-4">
        <button
          onClick={navigateToAddMou}
          className="bg-teal-500 font-bold text-white px-4 py-2 rounded-md"
        >
          Add Mou +
        </button>
      </div>

      <div className="overflow-x-auto bg-white shadow-lg rounded-lg border-t-4 border-teal-500">
        <table className="min-w-full table-auto border-collapse relative">
          <thead className="bg-[#00BFA5] text-white">
            <tr>
              <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">List Head</th>
              <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">Link</th>
              <th className="px-2 py-3 text-center text-xs sm:text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mous.map((mou) => (
              <tr key={mou._id} className="border-b hover:bg-teal-50 transition duration-200 ease-in-out">
                <td className="px-2 py-3 text-xs sm:text-sm text-gray-700 font-medium">{mou.listHead}</td>
                <td className="px-2 py-3 text-xs sm:text-sm text-gray-700 font-medium">
                  <a href={mou.link} target="_blank" rel="noopener noreferrer">
                    {mou.link}
                  </a>
                </td>
                <td className="px-2 py-3 text-center space-x-1 sm:space-x-3">
                  <button
                    onClick={() => handleDelete(mou._id)}
                    className="text-red-500 hover:text-red-700 transition duration-300"
                    title="Delete Mou"
                  >
                    <FaTrash size={16} />
                  </button>
                  <button
                    onClick={() => handleUpdate(mou)}
                    className="text-blue-500 hover:text-blue-700 transition duration-300"
                    title="Update Mou"
                  >
                    <FaEdit size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-1/2 p-6 space-y-6">
            <h2 className="text-xl font-bold text-teal-500">Update Mou</h2>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-semibold">List Head</label>
                <input
                  type="text"
                  value={selectedMou?.listHead || ""}
                  onChange={(e) =>
                    setSelectedMou({ ...selectedMou, listHead: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold">Link</label>
                <input
                  type="text"
                  value={selectedMou?.link || ""}
                  onChange={(e) =>
                    setSelectedMou({ ...selectedMou, link: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSubmit}
                className="bg-teal-500 text-white px-4 py-2 rounded-md"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageMouList;
